import { hasValue, isNullable, isEmpty } from "@cak-legacy/core-utils";

export namespace Json {
  type Merge<T> = T extends object ? (T extends any[] ? MergeArrays<T> : MergeObjects<T>) : T;
  type MergeArrays<T> = T extends (infer U)[] ? MergeUnions<U>[] : Merge<T>;
  type MergeObjects<T> = T extends object ? { [K in keyof T]: MergeUnions<T[K]> } : T;
  type MergeUnions<T> = (T extends any ? (arg: T) => void : never) extends (arg: infer U) => void ? U : never;

  type Simplify<T> = T extends Record<string, any> ? { [K in keyof T]: Simplify<T[K]> } : T;

  type Handlers = {
    array: <T extends any[] = any[]>(value: T) => any;
    object: <T extends Record<string, any> = Record<string, any>>(value: T) => any;
    default: <T extends any = any>(value?: T) => any;
  };

  export const handle = (value: any, handlers: Handlers) => {
    if (isNullable(value)) return handlers.default();
    if (Array.isArray(value)) return handlers.array(value);
    if (typeof value === "object") return handlers.object(value);
    return handlers.default(value);
  };

  type Flat<T, Prefix extends string = "", Depth extends number[] = []> = Depth["length"] extends 10
    ? {}
    : {
        [K in keyof T & string]: T[K] extends Array<infer U>
          ? U extends object
            ? Flat<U, `${Prefix}${K}.${number}.`, [0, ...Depth]>
            : Merge<{ [Key in `${Prefix}${K}.${number}`]: U }>
          : T[K] extends object
          ? Flat<T[K], `${Prefix}${K}.`, [0, ...Depth]>
          : Merge<{ [Key in `${Prefix}${K}`]: T[K] }>;
      }[keyof T & string];

  export const flat = <T extends Record<string, any>>(data: T): Simplify<Flat<T>> => {
    const result: Record<string, any> = {};

    const _flat = (data: any, prefix: string = "") => {
      Object.entries(data).forEach(([key, value]) => {
        const property = prefix ? `${prefix}.${key}` : key;

        handle(value, {
          array: (collection) => {
            _flat(collection, property);
          },
          object: (record) => {
            _flat(record, property);
          },
          default: (item) => {
            result[property] = item ?? undefined;
          }
        });
      });
    };

    _flat(data);

    return result as Simplify<Flat<T>>;
  };

  type Nest<T> = Merge<{
    [K in keyof T as K extends `${infer Section}.${infer Rest}`
      ? Section
      : K]: K extends `${infer Section}.${infer Rest}` ? Nest<{ [Key in Rest]: T[K] }> : T[K];
  }>;

  export const nest = <T extends Record<string, any>>(data: T): Simplify<Nest<T>> => {
    const result: Record<string, any> = {};

    Object.entries(data).forEach(([key, value]) => {
      const sections = key.split(".");

      sections.reduce((nested, section, index) => {
        const nextSection = sections.at(index + 1);

        if (nextSection) {
          const initial = nested[section] ?? isNaN(parseInt(nextSection)) ? {} : [];
          nested[section] = nested[section] ?? initial;

          return nested[section];
        }

        handle(value, {
          array: (collection) => {
            nested[section] = collection.map(nest);
          },
          object: (record) => {
            nested[section] = nest(record);
          },
          default: (item) => {
            nested[section] = item;
          }
        });

        return nested;
      }, result);
    });

    return result as Simplify<Nest<T>>;
  };

  type Clean<T> = T;

  export const clean = <T extends Record<string, any>>(data: T): Simplify<Clean<T>> => {
    return handle(data, {
      array: (collection) => {
        const list = collection.map(clean).filter(hasValue);
        return hasValue(list) ? list : undefined;
      },
      object: (record) => {
        const value = Object.entries(record).reduce((cleaned, [key, value]) => {
          if (isEmpty(value)) return cleaned;

          const cleanedValue = clean(value);
          if (isEmpty(cleanedValue)) return cleaned;

          cleaned[key] = cleanedValue;
          return cleaned;
        }, {} as Record<string, any>);

        return hasValue(value) ? value : undefined;
      },
      default: (item) => {
        return hasValue(item) ? item : undefined;
      }
    }) as Simplify<Clean<T>>;
  };
}
